import React, { useEffect, useState, useRef } from "react";
import {
  currentDateFormat,
  exportToExcel,
  exportFullTableToPDF,
} from "../utils";
import {
  TRIP_LIST_V1,
  GET_DRIVER_LIST,
  GET_ALL_PAYER,
  GET_SCHEPASSENGER_LIST,
} from "../constant";
import TripTableV1 from "../components/tables/TripTableV1";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setLoading } from "../redux/loaderAction";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SearchComponent from "../components/SearchComponent";
import Swal from "../messageBox/customSweetAlert";
import moment from "moment";

const PAGE_LIMIT = 10;
const PRIMARY_COLOR = "#F4A442";
const SECONDARY_COLOR = "#2D5A50";

function TripListV1() {
  const tableRef = useRef();
  const dispatch = useDispatch();
  const [currentDate] = useState(new Date());
  const [tripList, setTripList] = useState([]);
  const [reload, setReload] = useState(false);
  const [payerList, setPayerList] = useState([]);
  const [driverList, setDriverList] = useState([]);
  const [payer, setPayer] = useState("0");
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengerList, setPassengerList] = useState([]);
  const [tripStatus, setTripStatus] = useState("0");
  const [errors, setErrors] = useState({});
  const [showAllRowsForExport, setShowAllRowsForExport] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: PAGE_LIMIT,
  });

  const fetchTripsPage = async (page = 1) => {
    const params = {
      startDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : "",
      endDate: toDate ? moment(toDate).format("YYYY-MM-DD") : "",
      passengerId: selectedPassenger?.value?.user_id || "",
      payerId: payer === "0" ? "" : payer,
      driverId: selectedDriver?.value?.user_id || "",
      status: tripStatus || "0",
      page,
      limit: PAGE_LIMIT,
    };
    dispatch(setLoading(true));
    try {
      const response = await axios.get(TRIP_LIST_V1, { params });
      const ok =
        response?.data?.success === true ||
        response?.data?.status === 200 ||
        response?.data?.status === "200";
      if (response?.status === 200 && ok) {
        const rows = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        setTripList(rows);
        const p = response?.data?.pagination || {};
        setPagination({
          currentPage: p.currentPage ?? page,
          totalPages: Math.max(1, p.totalPages ?? 1),
          totalRecords: p.totalRecords ?? response?.data?.count ?? rows.length,
          recordsPerPage: p.recordsPerPage ?? PAGE_LIMIT,
        });
      } else {
        setTripList([]);
      }
    } catch (error) {
      console.error("Error fetching trips v1:", error);
      setTripList([]);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchTripsPage(1);
    const loadUser = async () => {
      const [drivers, payers, passengers] = await Promise.all([
        axios.get(GET_DRIVER_LIST),
        axios.get(GET_ALL_PAYER),
        axios.get(GET_SCHEPASSENGER_LIST),
      ]);
      setDriverList(drivers.data || []);
      setPayerList(payers.data || []);
      setPassengerList(passengers.data || []);
    };
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const handleSearch = async () => {
    if (errors.from_date || errors.to_date) {
      Swal.fire(
        "Error",
        "Please correct the date errors before searching",
        "error",
      );
      return false;
    }
    setIsSearchLoading(true);
    try {
      await fetchTripsPage(1);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleDeleteSuccess = () => {
    setReload((prev) => !prev);
    Swal.fire({
      title: "Success!",
      text: "Trip Deleted Successfully",
      icon: "success",
      confirmButtonText: "OK",
    });
  };

  const driverListWithFullName = driverList.map((driver) => ({
    ...driver,
    full_name:
      `${driver.first_name} ${driver.middle_name || ""} ${driver.last_name || ""}`
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase(),
  }));
  const passengerListWithFullName = passengerList.map((passenger) => ({
    ...passenger,
    full_name:
      `${passenger.first_name} ${passenger.middle_name || ""} ${passenger.last_name || ""} ${passenger.passenger?.pass_pmi_no || ""}`
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase(),
  }));

  const filterPayerName =
    payer === "0"
      ? "All"
      : payerList.find((p) => String(p.id) === String(payer))?.payer_name || "";
  const filterDriverName = selectedDriver?.label || "All";
  const filterPassengerName = selectedPassenger?.label || "All";
  const filterTripStatusStr =
    tripStatus === "0" ? "All" : tripStatus === "1" ? "Completed" : "Pending";
  const filterFromDateStr = fromDate
    ? moment(fromDate).format("MM/DD/YYYY")
    : "";
  const filterToDateStr = toDate ? moment(toDate).format("MM/DD/YYYY") : "";
  const { currentPage: pg, totalPages, totalRecords, recordsPerPage } = pagination;
  const startIndex = (pg - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + (tripList?.length || 0), totalRecords);

  const handlePageChange = (newPage) => {
    if (newPage !== pg) {
      fetchTripsPage(newPage);
    }
  };

  const getPageNumbers = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "ellipsis", totalPages];
  };

  const pageNumbers = getPageNumbers();

  return (
    <>
      <section style={{ borderBottom: "solid 1px #eee", marginTop: "0px" }}>
        <div className="d-flex justify-content-between form-container">
          <div>
            <h2 className="fs-5 font-weight-600">Trip List</h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>
      <section className="filter-section">
        <div
          className="d-flex justify-content-between filter-container"
          style={{ background: "rgb(205 228 218 / 29%)" }}
        >
          <div className="schedule-filter">
            <div className="me-2">
              <SearchComponent
                data={passengerListWithFullName}
                searchFields={["full_name"]}
                placeholder="Search Passenger"
                label="Passenger"
                onSelect={setSelectedPassenger}
              />
            </div>
            <div className="me-2">
              <SearchComponent
                data={driverListWithFullName}
                searchFields={["full_name"]}
                placeholder="Search Driver"
                label="Driver"
                onSelect={setSelectedDriver}
              />
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>Payor</label>
              <select
                className="form-select"
                value={payer}
                onChange={(e) => setPayer(e.target.value)}
              >
                <option value="0">All</option>
                {payerList.map((py) => (
                  <option key={py.id} value={py.id}>
                    {py.payer_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>Status</label>
              <select
                className="form-select"
                value={tripStatus}
                onChange={(e) => setTripStatus(e.target.value)}
              >
                <option value="0">All</option>
                <option value="1">Completed</option>
                <option value="2">Pending</option>
              </select>
            </div>
            <div className="me-2 date-container">
              <label style={{ display: "block" }}>From</label>
              <DatePicker
                selected={fromDate}
                onChange={setFromDate}
                dateFormat="MM/dd/yyyy"
                className="datepicker form-control hasDatepicker"
              />
            </div>
            <div className="me-2 date-container">
              <label style={{ display: "block" }}>To</label>
              <DatePicker
                selected={toDate}
                onChange={setToDate}
                dateFormat="MM/dd/yyyy"
                className="datepicker form-control hasDatepicker"
              />
            </div>
            <div>
              <label style={{ display: "block" }}>&nbsp;</label>
              <button
                className="btn btn-primary rounded-1"
                type="button"
                style={{
                  background: "#31876f",
                  border: "unset",
                  height: "38px",
                  color: "#fff",
                  padding: "7px",
                }}
                onClick={handleSearch}
                disabled={isSearchLoading}
              >
                {isSearchLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Searching...
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>
            <span>
              <i
                className="pi pi-file-excel"
                title="Export to Excel"
                onClick={() =>
                  exportToExcel(
                    document.getElementById("trip-table"),
                    "TripList",
                  )
                }
                style={{ fontSize: "24px", color: "green", cursor: "pointer" }}
              ></i>
              <i
                className="pi pi-file-pdf"
                title="Download PDF"
                style={{
                  fontSize: "24px",
                  color: "red",
                  cursor: "pointer",
                  marginLeft: "10px",
                }}
                onClick={() => {
                  setShowAllRowsForExport(true);
                  setTimeout(() => {
                    exportFullTableToPDF(
                      tableRef,
                      setShowAllRowsForExport,
                      "trip_table.pdf",
                    );
                    setShowAllRowsForExport(false);
                  }, 100);
                }}
              ></i>
            </span>
          </div>
        </div>
      </section>
      <section>
        <div className="table-responsive">
          <TripTableV1
            ref={tableRef}
            tableData={tripList}
            setTableData={setTripList}
            onDeleteSuccess={handleDeleteSuccess}
            filterPayer={filterPayerName}
            filterDriver={filterDriverName}
            filterPassenger={filterPassengerName}
            filterTripStatusStr={filterTripStatusStr}
            filterFromDate={filterFromDateStr}
            filterToDate={filterToDateStr}
          />
        </div>
      </section>
      {pagination.totalPages > 1 && (
        <div
          className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 px-4 py-3 mt-0"
          style={{
            backgroundColor: "rgba(248, 249, 250, 0.8)",
            border: "1px solid #e9ecef",
            borderTop: "none",
            borderRadius: "0 0 0.25rem 0.25rem",
          }}
        >
          <p className="text-sm text-muted mb-0">
            Showing{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {tripList?.length ? startIndex + 1 : 0}-{endIndex}
            </span>{" "}
            of{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {totalRecords}
            </span>{" "}
            results
          </p>
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: pg === 1 ? 0.35 : 1 }}
              disabled={pg === 1}
              onClick={() => handlePageChange(1)}
              title="First page"
              aria-label="First page"
            >
              <i className="pi pi-angle-double-left" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none me-1"
              style={{ opacity: pg === 1 ? 0.35 : 1 }}
              disabled={pg === 1}
              onClick={() => handlePageChange(pg - 1)}
              title="Previous page"
              aria-label="Previous page"
            >
              <i className="pi pi-angle-left" />
            </button>
            <div className="d-flex align-items-center gap-1">
              {pageNumbers.map((p, i) =>
                p === "ellipsis" ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-muted">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className="btn btn-sm fw-semibold rounded"
                    style={{
                      minWidth: 32,
                      height: 32,
                      backgroundColor: p === pg ? PRIMARY_COLOR : "transparent",
                      color: p === pg ? "#fff" : "#495057",
                      border: "none",
                      boxShadow: p === pg ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                    }}
                    onClick={() => handlePageChange(p)}
                    aria-label={`Page ${p}`}
                    aria-current={p === pg ? "page" : undefined}
                  >
                    {p}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none ms-1"
              style={{ opacity: pg === totalPages ? 0.35 : 1 }}
              disabled={pg === totalPages}
              onClick={() => handlePageChange(pg + 1)}
              title="Next page"
              aria-label="Next page"
            >
              <i className="pi pi-angle-right" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: pg === totalPages ? 0.35 : 1 }}
              disabled={pg === totalPages}
              onClick={() => handlePageChange(totalPages)}
              title="Last page"
              aria-label="Last page"
            >
              <i className="pi pi-angle-double-right" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default TripListV1;
